import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, skipWhile, switchMap } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { Store } from '@ngxs/store';
import { MenuStructure } from '@nimbus-library/features/menu';
import { IMenuProviderService } from '@nimbus-library/features/menu/services/contracts/i-menu-provider.service';
import { MenuViewModel } from '@nimbus-library/features/menu/models/menu.view-model';
import { AuthenticationFilter } from '@nimbus-global/features/menu/services/filters/authentication.filter';
import { TeamFilter } from '@nimbus-global/features/menu/services/filters/team.filter';
import { MenuMapper } from '@nimbus-library/features/menu/services/mapper/nimbus-menu.mapper';
import { ProvisioningFilter } from '@nimbus-portal/features/menu/services/filters/provisioning.filter';
import { ProvisioningTeamStatusAnswer } from '@nimbus-global/features/settings/models/provisioning-settings/i-provisioning-team-status.enum';
import { NavigationFilter } from '@nimbus-library/features/menu/services/filters/navigation.filter';
import { Routes } from '@nimbus-portal/features/menu/models/consts/routes.consts';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { LicenseFilter } from '@nimbus-global/features/menu/services/filters/license.filter';
import { NimbusLicense } from '@nimbus-global/features/shared/services/domain/models/license.model';
import { PermissionFilter } from '@nimbus-global/features/menu/services/filters/permission.filter';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { MySessionsNotificationService } from '@nimbus-portal/features/my-sessions/services/my-sessions-notification.service';
import { VoicemailsNewIndicatorService } from '@nimbus-portal/features/voicemails/services/voicemails-new-indicator.service';
import { HistoricalSessionsEnabledFilter } from '@nimbus-portal/features/menu/services/filters/historical-sessions-enabled.filter';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { CombineFilter } from '@nimbus-global/features/menu/services/filters/combine.filter';
import { RoleFilter } from '@nimbus-global/features/menu/services/filters/role.filter';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { FeatureFlippingFilterFactory } from '@nimbus-global/features/menu/services/filters/feature-flipping.filter';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { CiUnifiedSessionsService } from '@nimbus-portal/features/customer-insights/services/ci-unified-sessions.service';
import { RequestFilter } from '@nimbus-global/features/menu/services/filters/request.filter';
import { UIProfileFilter } from '@nimbus-portal/features/ui-profile/filters/ui-profile.filter';
import { UiProfilePortalState } from '@nimbus-portal/features/ui-profile/store/ui-profile-portal.state';
import { PortalPage } from '@nimbus-global/features/ui-profile/models/portal-page.enum';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { GlobalPermissionsModelFilter } from '@nimbus-global/features/menu/services/filters/global-permissions-model.filter';
import { IMenuIndicator, MenuEntryType } from '@nimbus-library/features/menu';
import { orderMenuEntriesByProfile } from '@nimbus-portal/features/menu/services/ui-profile-menu-orderer';
import { TASK_FLOW_PERMISSION_SETS } from '@nimbus-portal/features/task-flow-visualizer/models/task-flow.constants';

@Injectable()
export class MenuProviderService extends IMenuProviderService {
  private readonly provisioningFilter = ProvisioningFilter.for([ProvisioningTeamStatusAnswer.Active]);
  private readonly licenseContactCenterFilter = LicenseFilter.for(NimbusLicense.ContactCenter);
  private readonly licenseAttendantConsoleFilter = LicenseFilter.for(NimbusLicense.AttendantConsole);

  private readonly pageMyOverviewFilter = PermissionFilter.for({
    permission: NimbusPermission.ViewMyOverviewMarker,
    ignoreTeamSelection: true,
  });
  private readonly pageMySessionsFilter = PermissionFilter.for({
    permission: NimbusPermission.ViewMySessionsMarker,
    ignoreTeamSelection: true,
  });
  private readonly pageDashboardFilter = PermissionFilter.for({
    permission: NimbusPermission.ViewMyServicesDashboardMarker,
    ignoreTeamSelection: false,
  });
  private readonly pageReportingFilter = PermissionFilter.for({
    permission: NimbusPermission.ViewMyServicesDashboardMarker,
    ignoreTeamSelection: false,
  });
  private readonly pageFlexibleDashboardFilter = PermissionFilter.for({
    permission: NimbusPermission.ViewFlexibleDashboardsMarker,
    ignoreTeamSelection: true,
  });
  private readonly pageMyServicesFilter = PermissionFilter.for({
    permissions: [NimbusPermission.ViewMyServicesMyServicesMarker, NimbusPermission.ServicesRead],
    ignoreTeamSelection: true,
  });
  private readonly pageTeamSessionsFilter = HistoricalSessionsEnabledFilter.for({
    permission: NimbusPermission.ViewMyServicesHistoricalReportingMarker,
    ignoreTeamSelection: true,
  });
  private readonly pageAttendantConsoleFilter = PermissionFilter.for({
    permission: NimbusPermission.ViewAttendantConsoleMarker,
    ignoreTeamSelection: true,
  });
  private readonly pageVoicemailsFilter = PermissionFilter.for({
    permissions: [NimbusPermission.MedialibraryListCallRecords, NimbusPermission.ServicesRead],
  });
  private readonly pageTaskFlowVisualizerFilter = PermissionFilter.for({
    permission: NimbusPermission.ReadServiceStatisticsReporting,
    ignoreTeamSelection: false,
  });
  private readonly pageTaskFlowVisualizerHistoryFilter = PermissionFilter.for({
    permission: NimbusPermission.ReadServiceHistoricalStatisticsReporting,
    ignoreTeamSelection: false,
  });
  private readonly pageTaskFlowVisualizerRoleFilter = RoleFilter.for({
    permissionSet: TASK_FLOW_PERMISSION_SETS,
    ignoreTeamSelection: false,
  });
  private readonly pageSettingsFilter = PermissionFilter.for({
    permissions: [NimbusPermission.ViewMyServicesSettingsMarker, NimbusPermission.ServicesRead],
    ignoreTeamSelection: false,
  });
  private readonly pageProvisioningSettingsFilter = CombineFilter.for({
    filters: [
      RoleFilter.for({ permissionSet: [NimbusPermissionSet.OuAdmin, NimbusPermissionSet.WorkflowAdminLimited], ignoreTeamSelection: true }),
      this.provisioningFilter,
    ],
    condition: 'or',
  });
  private readonly pageCustomerOverviewFilter = PermissionFilter.for({
    permission: NimbusPermission.CustomerOverviewRead,
    ignoreTeamSelection: true,
  });
  private readonly pageConfigurationFilter = PermissionFilter.for({
    permission: NimbusPermission.ViewConfigurationMarker,
    ignoreTeamSelection: true,
  });

  private readonly uiProfileHomeFilter = UIProfileFilter.for(PortalPage.Home);
  private readonly uiProfileMySessionFilter = UIProfileFilter.for(PortalPage.MySession);
  private readonly uiProfileDashboardsFilter = UIProfileFilter.for(PortalPage.Dashboards);
  private readonly uiProfileServicesOverviewFilter = UIProfileFilter.for(PortalPage.ServicesOverview);
  private readonly uiProfileLiveViewFilter = UIProfileFilter.for(PortalPage.LiveView);
  private readonly uiProfileStatisticsFilter = UIProfileFilter.for(PortalPage.Statistics);
  private readonly uiProfileSessionListFilter = UIProfileFilter.for(PortalPage.SessionList);
  private readonly uiProfileSettingsFilter = UIProfileFilter.for(PortalPage.Settings);
  private readonly uiProfileAttendantConsoleFilter = UIProfileFilter.for(PortalPage.AttendantConsole);
  private readonly uiProfileCustomerInsightsFilter = UIProfileFilter.for(PortalPage.CustomerInsights);
  private readonly uiProfileConfigurationFilter = UIProfileFilter.for(PortalPage.Configuration);
  private readonly uiProfileVoicemailsFilter = UIProfileFilter.for(PortalPage.Voicemails);
  private readonly uiProfileCompanionFilter = UIProfileFilter.for(PortalPage.Companion);
  private readonly uiProfileTaskFlowVisualizerFilter = UIProfileFilter.for(PortalPage.TaskFlowVisualizer);

  private readonly mainMenu: MenuStructure;

  private readonly personalMenu: MenuStructure;

  private readonly mapper: MenuMapper;

  public constructor(
    private readonly injector: Injector,
    private readonly featureFlippingService: FeatureFlippingService,
    private readonly microsoftTeamsContextAccessor: IMicrosoftTeamsContextAccessor,
    private readonly mySessionsNotificationService: MySessionsNotificationService,
    private readonly voicemailsNewIndicatorService: VoicemailsNewIndicatorService,
    private readonly unifiedSessionsService: CiUnifiedSessionsService,
    private readonly permissionManagerService: IPermissionManagerService,
    private readonly store: Store,
  ) {
    super();
    this.mapper = new MenuMapper();

    this.mainMenu = this.createMainMenu();
    this.personalMenu = this.createPersonalMenu();
  }

  private createMainMenu(): MenuStructure {
    const featureFlippingFilterFactory = new FeatureFlippingFilterFactory(this.featureFlippingService);

    return {
      entries: [
        { icon: ICONS.DASHBOARD, route: Routes.dashboard, filters: [this.pageDashboardFilter, AuthenticationFilter] },
        {
          icon: ICONS.REPORTING,
          route: Routes.reporting,
          filters: [this.pageReportingFilter, AuthenticationFilter, this.provisioningFilter],
        },
        {
          icon: ICONS.VOICEMAIL,
          route: Routes.personalVoicemails,
          indicator: this.createVoicemailsIndicator(),
          filters: [
            TeamFilter,
            this.pageVoicemailsFilter,
            featureFlippingFilterFactory.forFeatureInFeatureManagement(Feature.ManagerVoicemails),
            this.uiProfileVoicemailsFilter,
          ],
          ...this.uiProfileCustomization(PortalPage.Voicemails),
        },
        {
          icon: ICONS.TASK_FLOW_VISUALIZER,
          route: Routes.taskFlowVisualizer,
          filters: [
            TeamFilter,
            this.pageTaskFlowVisualizerFilter,
            this.pageTaskFlowVisualizerHistoryFilter,
            this.pageTaskFlowVisualizerRoleFilter,
            featureFlippingFilterFactory.forFeatureInFeatureManagement(Feature.TaskFlowVisualizer),
            this.uiProfileTaskFlowVisualizerFilter,
          ],
          ...this.uiProfileCustomization(PortalPage.TaskFlowVisualizer),
        },
        {
          icon: ICONS.SETTINGS,
          route: Routes.settings,
          filters: [AuthenticationFilter, this.provisioningFilter, this.pageSettingsFilter],
        },
        {
          icon: ICONS.COG,
          route: Routes.manage,
          filters: [AuthenticationFilter, this.provisioningFilter, this.pageConfigurationFilter],
        },
        { icon: ICONS.USER_SETTINGS, route: Routes.userPreferences, renderAs: MenuEntryType.UserPreferences, filters: [] },
      ],
      footerEntries: [],
      brand: { icon: ICONS.BRAND, filters: [], route: Routes.brand },
      filters: [NavigationFilter],
      separatorPositions: [Routes.manage.name],
    };
  }

  /**
   * Builds the <code>customImageUrl$</code> / <code>customTooltip$</code> observables to spread
   * onto a menu entry that is gated by {@link UIProfileFilter} for the given page. When the
   * admin has set a custom icon or tooltip on the user's profile, the menu-item template swaps
   * the default glyph / translation for the admin-provided values.
   */
  private uiProfileCustomization(page: PortalPage): {
    customImageUrl$: Observable<string | null>;
    customTooltip$: Observable<string | null>;
  } {
    const customization$ = this.store
      .select(UiProfilePortalState.customizationFor(page))
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    return {
      customImageUrl$: customization$.pipe(map((customization) => customization?.imageUrl ?? null)),
      customTooltip$: customization$.pipe(map((customization) => customization?.tooltipText ?? null)),
    };
  }

  public getMenu(): Observable<MenuViewModel> {
    return this.microsoftTeamsContextAccessor.getContext$().pipe(
      skipWhile((context) => !context),
      switchMap(() => {
        const isPersonalMenu = this.microsoftTeamsContextAccessor.isStandAlone() || this.microsoftTeamsContextAccessor.isStaticTab();
        if (!isPersonalMenu) {
          return of(this.mapper.handle(this.mainMenu, this.injector));
        }

        // Rebuild in the UI-profile `visiblePages` order (loads asynchronously) instead of the
        // static declaration order.
        return this.store.select(UiProfilePortalState.visiblePages).pipe(
          distinctUntilChanged(isEqual),
          map((visiblePages) =>
            this.mapper.handle(
              { ...this.personalMenu, entries: orderMenuEntriesByProfile(this.personalMenu.entries, visiblePages) },
              this.injector,
            ),
          ),
        );
      }),
    );
  }

  private createVoicemailsIndicator(): IMenuIndicator {
    return {
      displayed$: this.voicemailsNewIndicatorService.hasNewVoicemails$,
      class: 'menu-item-indicator-dot',
    };
  }

  private createPersonalMenu(): MenuStructure {
    const featureFlippingFilterFactory = new FeatureFlippingFilterFactory(this.featureFlippingService);
    const getUserMySessionsMenuIndicatorCount$ = this.mySessionsNotificationService
      .getUserMySessionsMenuIndicatorCount$()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    return {
      entries: [
        {
          icon: ICONS.PERSONALHOME,
          route: Routes.personalOverview,
          filters: [AuthenticationFilter, this.pageMyOverviewFilter, this.uiProfileHomeFilter],
          ...this.uiProfileCustomization(PortalPage.Home),
        },
        {
          icon: ICONS.MYSESSIONS,
          route: Routes.personalMySessions,
          indicator: {
            displayed$: getUserMySessionsMenuIndicatorCount$.pipe(map((x) => !!x)),
            displayedCount$: getUserMySessionsMenuIndicatorCount$,
            class: 'menu-item-indicator-my-sessions',
          },
          filters: [AuthenticationFilter, this.pageMySessionsFilter, this.uiProfileMySessionFilter],
          ...this.uiProfileCustomization(PortalPage.MySession),
        },
        {
          icon: ICONS.DASHBOARD,
          route: Routes.dashboards,
          filters: [this.licenseContactCenterFilter, this.pageFlexibleDashboardFilter, this.uiProfileDashboardsFilter],
          ...this.uiProfileCustomization(PortalPage.Dashboards),
        },
        {
          icon: ICONS.USERS,
          route: Routes.personal,
          subClass: 'sub-menu-level-1',
          filters: [AuthenticationFilter],
          subEntries: [
            {
              icon: ICONS.TEAMS,
              route: Routes.personalTeams,
              filters: [TeamFilter, this.pageMyServicesFilter, this.uiProfileServicesOverviewFilter],
              ...this.uiProfileCustomization(PortalPage.ServicesOverview),
            },
            {
              icon: ICONS.DASHBOARD_LIGHT,
              route: Routes.dashboard,
              filters: [TeamFilter, this.pageDashboardFilter, this.uiProfileLiveViewFilter],
              ...this.uiProfileCustomization(PortalPage.LiveView),
            },
            {
              icon: ICONS.REPORTING,
              route: Routes.reporting,
              filters: [TeamFilter, this.pageReportingFilter, this.provisioningFilter, this.uiProfileStatisticsFilter],
              ...this.uiProfileCustomization(PortalPage.Statistics),
            },
            {
              icon: ICONS.CONVERSATION_CONTEXT,
              route: Routes.teamSessions,
              filters: [TeamFilter, this.pageTeamSessionsFilter, this.provisioningFilter, this.uiProfileSessionListFilter],
              ...this.uiProfileCustomization(PortalPage.SessionList),
            },
            {
              icon: ICONS.VOICEMAIL,
              route: Routes.personalVoicemails,
              indicator: this.createVoicemailsIndicator(),
              filters: [
                TeamFilter,
                this.pageVoicemailsFilter,
                featureFlippingFilterFactory.forFeatureInFeatureManagement(Feature.ManagerVoicemails),
                this.uiProfileVoicemailsFilter,
              ],
              ...this.uiProfileCustomization(PortalPage.Voicemails),
            },
            {
              icon: ICONS.TASK_FLOW_VISUALIZER,
              route: Routes.taskFlowVisualizer,
              filters: [
                TeamFilter,
                this.pageTaskFlowVisualizerFilter,
                this.pageTaskFlowVisualizerHistoryFilter,
                this.pageTaskFlowVisualizerRoleFilter,
                featureFlippingFilterFactory.forFeatureInFeatureManagement(Feature.TaskFlowVisualizer),
                this.uiProfileTaskFlowVisualizerFilter,
              ],
              ...this.uiProfileCustomization(PortalPage.TaskFlowVisualizer),
            },
            {
              icon: ICONS.SETTINGS,
              route: Routes.settings,
              filters: [TeamFilter, this.pageSettingsFilter, this.pageProvisioningSettingsFilter, this.uiProfileSettingsFilter],
              ...this.uiProfileCustomization(PortalPage.Settings),
            },
          ],
        },
        {
          icon: ICONS.ATTENDANT_CONSOLE,
          route: Routes.personalAttendantconsole,
          filters: [
            AuthenticationFilter,
            this.pageAttendantConsoleFilter,
            this.licenseAttendantConsoleFilter,
            this.uiProfileAttendantConsoleFilter,
          ],
          ...this.uiProfileCustomization(PortalPage.AttendantConsole),
        },
        {
          icon: ICONS.OPERATOR_CUSTOMER,
          route: Routes.customerInsights,
          filters: [
            AuthenticationFilter,
            this.pageCustomerOverviewFilter,
            featureFlippingFilterFactory.forFeatureInFeatureManagement(Feature.CustomerJourney),
            new RequestFilter(this.unifiedSessionsService.isCustomerOverviewEnabled()),
            this.uiProfileCustomerInsightsFilter,
          ],
          ...this.uiProfileCustomization(PortalPage.CustomerInsights),
        },
        {
          icon: ICONS.COG,
          route: Routes.manage,
          filters: [AuthenticationFilter, this.pageConfigurationFilter, this.uiProfileConfigurationFilter],
          ...this.uiProfileCustomization(PortalPage.Configuration),
        },
        { icon: ICONS.USER_SETTINGS, route: Routes.userPreferences, renderAs: MenuEntryType.UserPreferences, filters: [] },
        {
          icon: ICONS.COMPANION,
          route: Routes.companionConversation,
          renderAs: MenuEntryType.Companion,
          filters: [
            featureFlippingFilterFactory.forFeatureInFeatureManagement(Feature.CompanionConversationPortal),
            new GlobalPermissionsModelFilter(
              this.permissionManagerService,
              (model) => model?.tenant?.allowCompanionConversationForPortal ?? false,
            ),
            this.uiProfileCompanionFilter,
          ],
          ...this.uiProfileCustomization(PortalPage.Companion),
        },
      ],
      footerEntries: [],
      brand: { icon: ICONS.BRAND, filters: [], route: Routes.brand },
      filters: [NavigationFilter],
      separatorPositions: [Routes.manage.name, Routes.companionConversation.name],
    };
  }
}
